// bluaIQ — M8: Insurance Tracking Module
// GL, Builder's Risk, Workers Comp with expiration alerts

import { apiFetch } from '../utils/api'
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@clerk/clerk-react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Shield, AlertTriangle, CheckCircle, Clock, Calendar, Plus, X } from 'lucide-react'
import { isDemoProject } from '../utils/demoData'

// ── TYPES ─────────────────────────────────────────

interface Insurance {
  id: string
  type: string
  provider: string
  policyNumber?: string
  coverageAmount?: number
  premium?: number
  startDate: string
  endDate: string
  notes?: string
  // Optional Spanish translation of `notes`. Demo-only: present on
  // DEMO_INSURANCE rows so the policy card notes render in Spanish
  // when the user switches to ES. Real user-saved Insurance rows
  // from /api/projects/:id/insurance will not have this field, and
  // the renderer falls back to `notes` so user-entered text is
  // shown verbatim (never translated by bluaIQ).
  notesEs?: string
  // PR-NEW: server-computed status (EXPIRED | EXPIRING_SOON |
  // ACTIVE) from src/server/utils/insuranceStatus.ts. Optional
  // so legacy clients that haven't refreshed past this PR can
  // still render — the card falls back to a local date-based
  // computation when this field is missing.
  derivedStatus?: 'EXPIRED' | 'EXPIRING_SOON' | 'ACTIVE'
}

const INSURANCE_TYPES: Record<string, { en: string; es: string; color: string; icon: string }> = {
  GENERAL_LIABILITY: { en: 'General Liability', es: 'Responsabilidad General', color: '#3B82F6', icon: '🛡️' },
  BUILDERS_RISK:     { en: "Builder's Risk",   es: 'Seguro de Construcción',  color: '#F59E0B', icon: '🏗️' },
  WORKERS_COMP:      { en: 'Workers Comp',     es: 'Compensación Laboral',    color: '#00BCD4', icon: '👷' },
}

// ── DEMO DATA ───────────────────────────────────

const DEMO_INSURANCE: Insurance[] = [
  {
    id: 'ins-1', type: 'GENERAL_LIABILITY', provider: 'ABC Insurance Group',
    policyNumber: 'GL-2026-88432', coverageAmount: 1000000, premium: 2800,
    startDate: '2025-12-01', endDate: '2026-04-05',
    notes: 'Per-occurrence limit $1M / Aggregate $2M',
    notesEs: 'Límite por ocurrencia $1M / Agregado $2M',
  },
  {
    id: 'ins-2', type: 'BUILDERS_RISK', provider: 'Texas Shield Insurance',
    policyNumber: 'BR-4521-LGY', coverageAmount: 285000, premium: 1450,
    startDate: '2026-01-15', endDate: '2026-09-15',
    notes: 'Coverage matches project budget — 4521 Legacy Dr',
    notesEs: 'Cobertura iguala el presupuesto del proyecto — 4521 Legacy Dr',
  },
  {
    id: 'ins-3', type: 'WORKERS_COMP', provider: 'SafeWork TX',
    policyNumber: 'WC-TX-2026-5567', coverageAmount: 500000, premium: 3200,
    startDate: '2026-01-01', endDate: '2026-12-31',
    notes: 'Covers all job sites in Collin & Denton County',
    notesEs: 'Cubre todos los sitios de trabajo en los condados de Collin y Denton',
  },
]

// ── COMPONENT ─────────────────────────────────────

export default function InsuranceModule() {
  const { getToken } = useAuth()
  const { projectId } = useParams()
  const { t, i18n } = useTranslation()
  const isEs = i18n.language === 'es'
  const queryClient = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [renewId, setRenewId] = useState<string | null>(null)

  // PR-NEW: any insurance mutation (create / renew) must
  // invalidate three caches so the derived calendar reminders
  // and dashboard KPI surface fresh data:
  //   * ['insurance', projectId]               — this module's card list
  //   * ['calendar-events', projectId]         — CalendarModule (5-tuple
  //                                              key; invalidation
  //                                              matches by prefix so
  //                                              every visible
  //                                              from/to/lang variant
  //                                              re-derives)
  //   * ['dashboard', projectId]               — Dashboard KPI
  //                                              (expiringInsurance)
  // Calling this from every onCreated / onRenewed callback keeps
  // every Insurance-derived surface in lockstep with the row.
  const invalidateInsuranceCaches = () => {
    queryClient.invalidateQueries({ queryKey: ['insurance', projectId] })
    queryClient.invalidateQueries({ queryKey: ['calendar-events', projectId] })
    queryClient.invalidateQueries({ queryKey: ['dashboard', projectId] })
  }

  const { data: apiInsurance, isLoading } = useQuery<Insurance[]>({
    queryKey: ['insurance', projectId],
    queryFn: async () => { const token = await getToken(); return apiFetch(`/api/projects/${projectId}/insurance`, { headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) } }).then(r => {
      if (!r.ok) throw new Error('API unavailable')
      return r.json()
    }) },
    retry: false,
  })
  const insurances = Array.isArray(apiInsurance) ? apiInsurance : (isDemoProject(projectId) ? DEMO_INSURANCE : [])

  if (isLoading) return <div className="p-5 animate-pulse"><div className="h-64 bg-[#ECEAE4] rounded-xl" /></div>

  const now = new Date()

  return (
    <div className="p-5 max-w-4xl mx-auto space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-[18px] font-[900] tracking-[-0.5px] text-[#1C1A17]">{t('Insurance')}</h2>
          <p className="text-[11px] font-[600] text-[#9E9890] mt-0.5">
            {t('Track policies and expiration dates')}
          </p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-1.5 bg-[#06539B] text-white text-[13px] font-[800] px-4 py-2.5 rounded-full hover:bg-[#14457E] transition-colors"
        >
          <Plus size={14} />
          {isEs ? 'Agregar Póliza' : 'Add Policy'}
        </button>
      </div>

      {/* Inline add form */}
      {showForm && <AddInsuranceForm projectId={projectId!} isEs={isEs} getToken={getToken} onClose={() => setShowForm(false)} onCreated={() => { setShowForm(false); invalidateInsuranceCaches() }} />}

      {/* Insurance cards */}
      <div className="space-y-3">
        {insurances?.map(ins => {
          const typeInfo = INSURANCE_TYPES[ins.type]
          const endDate = new Date(ins.endDate)
          const daysLeft = Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
          // PR-NEW: prefer the server-computed `derivedStatus`
          // when present. Legacy fallback: same date math the
          // card used before this PR, so clients running ahead
          // of the server (or in demo mode) still render
          // sensibly. Both paths agree on the same three buckets.
          const isExpired = ins.derivedStatus
            ? ins.derivedStatus === 'EXPIRED'
            : daysLeft < 0
          const isExpiring = ins.derivedStatus
            ? ins.derivedStatus === 'EXPIRING_SOON'
            : daysLeft >= 0 && daysLeft <= 30

          return (
            <div key={ins.id} className={`bg-white border rounded-xl overflow-hidden ${isExpired ? 'border-red-300' : isExpiring ? 'border-amber-300' : 'border-[#ECEAE4]'}`}>
              <div className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-[18px]">{typeInfo?.icon || '📋'}</span>
                    <div>
                      <h3 className="text-[13px] font-[800] text-[#1C1A17]">
                        {isEs ? typeInfo?.es : typeInfo?.en || ins.type}
                      </h3>
                      <span className="text-[10px] font-[600] text-[#9E9890]">{ins.provider}</span>
                    </div>
                  </div>

                  {/* Status badge */}
                  {isExpired ? (
                    <span className="flex items-center gap-1 text-[9px] font-[800] px-2.5 py-1 rounded-full bg-red-50 text-red-600">
                      <AlertTriangle size={10} /> {t('Expired')}
                    </span>
                  ) : isExpiring ? (
                    <span className="flex items-center gap-1 text-[9px] font-[800] px-2.5 py-1 rounded-full bg-amber-50 text-amber-600">
                      <Clock size={10} /> {daysLeft} {t('days left')}
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-[9px] font-[800] px-2.5 py-1 rounded-full bg-green-50 text-green-600">
                      <CheckCircle size={10} /> {t('Active')}
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {ins.policyNumber && (
                    <div>
                      <div className="text-[9px] font-[800] text-[#9E9890] uppercase tracking-[0.5px]">{t('Policy #')}</div>
                      <div className="text-[11px] font-[700]">{ins.policyNumber}</div>
                    </div>
                  )}
                  {ins.coverageAmount && (
                    <div>
                      <div className="text-[9px] font-[800] text-[#9E9890] uppercase tracking-[0.5px]">{t('Coverage')}</div>
                      <div className="text-[11px] font-[700]">${ins.coverageAmount.toLocaleString()}</div>
                    </div>
                  )}
                  {ins.premium && (
                    <div>
                      <div className="text-[9px] font-[800] text-[#9E9890] uppercase tracking-[0.5px]">{t('Premium')}</div>
                      <div className="text-[11px] font-[700]">${ins.premium.toLocaleString()}</div>
                    </div>
                  )}
                  {/* PR-#93 added an Effective/Inicia cell here.
                      Reverted in PR-NEW: the extra cell crowded
                      the data grid (grid-cols-2 md:grid-cols-4)
                      and pushed the Renew action below the fold
                      visually. The start date is still emitted as
                      an informational calendar event by
                      src/server/utils/insuranceCalendar.ts; the
                      card stays focused on expiration as the
                      primary operational field. */}
                  <div>
                    <div className="text-[9px] font-[800] text-[#9E9890] uppercase tracking-[0.5px]">{t('Expires')}</div>
                    <div className="text-[11px] font-[700] flex items-center gap-1">
                      <Calendar size={10} />
                      {endDate.toLocaleDateString(isEs ? 'es-MX' : 'en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </div>
                  </div>
                </div>

                {/* Expiration progress bar */}
                <div className="mt-3">
                  <div className="h-[3px] bg-[#ECEAE4] rounded-full overflow-hidden">
                    {(() => {
                      const start = new Date(ins.startDate).getTime()
                      const end = endDate.getTime()
                      const total = end - start
                      const elapsed = now.getTime() - start
                      const pct = Math.max(0, Math.min(100, (elapsed / total) * 100))
                      const color = isExpired ? '#EF4444' : isExpiring ? '#F59E0B' : typeInfo?.color || '#22C55E'
                      return <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
                    })()}
                  </div>
                </div>
              </div>

              {(isExpired || isExpiring) && (
                <div className={`px-4 py-2.5 border-t ${isExpired ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
                  <button
                    onClick={() => setRenewId(renewId === ins.id ? null : ins.id)}
                    className={`w-full text-[13px] font-[800] py-2 rounded-full text-white ${isExpired ? 'bg-red-500 hover:bg-red-600' : 'bg-amber-500 hover:bg-amber-600'} transition-colors`}
                  >
                    {t('Renew Now')}
                  </button>
                </div>
              )}
              {renewId === ins.id && (
                <RenewInsuranceForm
                  insurance={ins}
                  projectId={projectId!}
                  isEs={isEs}
                  getToken={getToken}
                  onClose={() => setRenewId(null)}
                  onRenewed={() => { setRenewId(null); invalidateInsuranceCaches() }}
                />
              )}
            </div>
          )
        })}
      </div>

      {(!insurances || insurances.length === 0) && (
        <div className="text-center py-12">
          <Shield size={36} className="mx-auto text-[#ECEAE4] mb-3" />
          <p className="text-[12px] font-[700] text-[#9E9890]">{t('No insurance policies')}</p>
          <p className="text-[10px] font-[600] text-[#9E9890] mt-1">{t('Add GL, Builder\'s Risk, and Workers Comp')}</p>
          <button
            onClick={() => setShowForm(true)}
            className="mt-4 inline-flex items-center gap-1.5 bg-[#06539B] text-white text-[13px] font-[800] px-5 py-2.5 rounded-full hover:bg-[#14457E] transition-colors"
          >
            <Plus size={14} />
            {isEs ? 'Agregar Póliza' : 'Add Policy'}
          </button>
        </div>
      )}
    </div>
  )
}

// ── ADD INSURANCE FORM ───────────────────────────

function AddInsuranceForm({ projectId, isEs, getToken, onClose, onCreated }: {
  projectId: string; isEs: boolean; getToken: () => Promise<string | null>; onClose: () => void; onCreated: () => void
}) {
  const [form, setForm] = useState({
    type: 'GENERAL_LIABILITY' as string,
    provider: '',
    policyNumber: '',
    coverageAmount: '',
    premium: '',
    startDate: new Date().toISOString().slice(0, 10),
    endDate: '',
    notes: '',
  })

  const mutation = useMutation({
    mutationFn: async () => {
      const token = await getToken()
      return apiFetch(`/api/projects/${projectId}/insurance`, {
        method: 'POST',
        headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: form.type,
          provider: form.provider,
          policyNumber: form.policyNumber || undefined,
          coverageAmount: form.coverageAmount ? Number(form.coverageAmount) : undefined,
          premium: form.premium ? Number(form.premium) : undefined,
          startDate: new Date(form.startDate).toISOString(),
          endDate: new Date(form.endDate).toISOString(),
          notes: form.notes || undefined,
        })
      }).then(r => r.json())
    },
    onSuccess: () => onCreated(),
  })

  const inputClass = "w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[14px] font-[600] focus:outline-none focus:border-[#06539B]"
  const labelClass = "text-[9px] font-[800] text-[#9E9890] uppercase tracking-[0.5px] mb-1"

  return (
    <div className="bg-white border border-[#ECEAE4] rounded-xl p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-[13px] font-[800]">{isEs ? 'Nueva Póliza' : 'New Policy'}</h3>
        <button onClick={onClose}><X size={16} className="text-[#9E9890]" /></button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <div className={labelClass}>{isEs ? 'Tipo' : 'Type'}</div>
          <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} className={inputClass}>
            {Object.entries(INSURANCE_TYPES).map(([key, val]) => (
              <option key={key} value={key}>{isEs ? val.es : val.en}</option>
            ))}
          </select>
        </div>
        <div>
          <div className={labelClass}>{isEs ? 'Aseguradora' : 'Provider'}</div>
          <input value={form.provider} onChange={e => setForm({ ...form, provider: e.target.value })} className={inputClass} placeholder={isEs ? 'p. ej. ABC Insurance Group' : 'e.g. ABC Insurance Group'} />
        </div>
        <div>
          <div className={labelClass}>{isEs ? 'No. Póliza' : 'Policy #'}</div>
          <input value={form.policyNumber} onChange={e => setForm({ ...form, policyNumber: e.target.value })} className={inputClass} />
        </div>
        <div>
          <div className={labelClass}>{isEs ? 'Cobertura' : 'Coverage'} ($)</div>
          <input type="number" value={form.coverageAmount} onChange={e => setForm({ ...form, coverageAmount: e.target.value })} className={inputClass} placeholder="1000000" />
        </div>
        <div>
          <div className={labelClass}>{isEs ? 'Fecha Inicio' : 'Start Date'}</div>
          <input type="date" value={form.startDate} onChange={e => setForm({ ...form, startDate: e.target.value })} className={inputClass} />
        </div>
        <div>
          <div className={labelClass}>{isEs ? 'Fecha Vencimiento' : 'End Date'}</div>
          <input type="date" value={form.endDate} onChange={e => setForm({ ...form, endDate: e.target.value })} className={inputClass} />
        </div>
      </div>
      <button
        onClick={() => mutation.mutate()}
        disabled={!form.provider || !form.endDate || mutation.isPending}
        className="w-full bg-[#06539B] text-white text-[13px] font-[800] py-2.5 rounded-full disabled:opacity-40"
      >
        {mutation.isPending ? '...' : (isEs ? 'Guardar Póliza' : 'Save Policy')}
      </button>
    </div>
  )
}

function RenewInsuranceForm({ insurance, projectId, isEs, getToken, onClose, onRenewed }: {
  insurance: Insurance; projectId: string; isEs: boolean; getToken: () => Promise<string | null>; onClose: () => void; onRenewed: () => void
}) {
  const today = new Date().toISOString().slice(0, 10)
  const oneYearLater = new Date(Date.now() + 365 * 86400000).toISOString().slice(0, 10)
  const [form, setForm] = useState({
    startDate: today,
    endDate: oneYearLater,
    premium: insurance.premium?.toString() || '',
    policyNumber: insurance.policyNumber || '',
  })

  const mutation = useMutation({
    mutationFn: async () => {
      const token = await getToken()
      const res = await apiFetch(`/api/projects/${projectId}/insurance/${insurance.id}`, {
        method: 'PUT',
        headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          startDate: new Date(form.startDate).toISOString(),
          endDate: new Date(form.endDate).toISOString(),
          premium: form.premium ? Number(form.premium) : undefined,
          policyNumber: form.policyNumber || undefined,
        })
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.error || `Renewal failed (${res.status})`)
      }
      return res.json()
    },
    onSuccess: () => onRenewed(),
    onError: (err: Error) => alert(err.message),
  })

  const inputClass = "w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[14px] font-[600] focus:outline-none focus:border-[#06539B]"
  const labelClass = "text-[9px] font-[800] text-[#9E9890] uppercase tracking-[0.5px] mb-1"

  return (
    <div className="border-t border-[#ECEAE4] p-4 bg-[#FAFAF8] space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-[13px] font-[800]">{isEs ? 'Renovar Póliza' : 'Renew Policy'}</h3>
        <button onClick={onClose}><X size={16} className="text-[#9E9890]" /></button>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className={labelClass}>{isEs ? 'Nueva Fecha Inicio' : 'New Start Date'}</div>
          <input type="date" value={form.startDate} onChange={e => setForm({ ...form, startDate: e.target.value })} className={inputClass} />
        </div>
        <div>
          <div className={labelClass}>{isEs ? 'Nueva Fecha Vencimiento' : 'New End Date'}</div>
          <input type="date" value={form.endDate} onChange={e => setForm({ ...form, endDate: e.target.value })} className={inputClass} />
        </div>
        <div>
          <div className={labelClass}>{isEs ? 'No. Póliza' : 'Policy #'}</div>
          <input value={form.policyNumber} onChange={e => setForm({ ...form, policyNumber: e.target.value })} className={inputClass} />
        </div>
        <div>
          <div className={labelClass}>{isEs ? 'Prima' : 'Premium'} ($)</div>
          <input type="number" value={form.premium} onChange={e => setForm({ ...form, premium: e.target.value })} className={inputClass} />
        </div>
      </div>
      <button
        onClick={() => mutation.mutate()}
        disabled={!form.endDate || mutation.isPending}
        className="w-full bg-green-600 text-white text-[13px] font-[800] py-2.5 rounded-full disabled:opacity-40 hover:bg-green-700 transition-colors"
      >
        {mutation.isPending ? '...' : (isEs ? 'Confirmar Renovación' : 'Confirm Renewal')}
      </button>
    </div>
  )
}
